import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appointmentBookingService } from '../services/appointmentBooking.service';
import { AppointmentBookingRequest, AppointmentBookingResponse } from '../types/appointmentBooking';

export interface TimeSlot {
  startTime: string;
  endTime: string;
  territoryId: string;
  resourceId: string;
  resourceName: string;
  remainingAppointments: number;
  attendeeLimit: number | null;
  workTypeGroupId: string; // Salesforce ID starting with 0VSD
}

interface CampaignMember {
  remoteObjectId: string;
  campaignId: number;
  contactId: number;
}

export const useAppointmentBooking = () => {
  const queryClient = useQueryClient();
  const { data: campaignMembers } = useQuery<CampaignMember[]>({
    queryKey: ['userCampaigns'],
    queryFn: async () => {
      const response = await appointmentBookingService.getMe();
      return response.campaignMembers || [];
    }
  });

  const { data: events } = useQuery({
    queryKey: ['events'],
    queryFn: () => appointmentBookingService.getEvents()
  });

  const mutation = useMutation<AppointmentBookingResponse, Error, AppointmentBookingRequest>({
    mutationFn: (bookingData) => appointmentBookingService.bookAppointment(bookingData),
    onSuccess: () => {
      // Invalidate queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['userCampaigns'] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
      // Invalidate appointment slots to refetch fresh availability after booking
      // This prevents booking conflicts when a user books multiple appointments without page refresh
      queryClient.invalidateQueries({ queryKey: ['appointmentsSlots'] });
    }
  });

  const getAvailableSlots = (appointmentId: string, location: string) => {
    return useQuery({
      queryKey: ['appointmentsSlots', appointmentId, location],
      queryFn: async () => {
        const response = await appointmentBookingService.getAvailableSlots(appointmentId, location);
        // Handle both formats: direct array or wrapped in data property
        return Array.isArray(response) ? { data: response } : response;
      },
      enabled: !!appointmentId && !!location
    });
  };

  const isAppointmentBooked = (appointmentId: string): boolean => {
    return campaignMembers?.some((member: CampaignMember) => member.remoteObjectId === appointmentId) || false;
  };

  return {
    bookAppointment: mutation.mutate,
    bookAppointmentAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
    getAvailableSlots,
    isAppointmentBooked,
    registeredEvents: campaignMembers || [],
    events: events || []
  };
};
